import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Session, buildAuthRedirectToUrl } from '@om/shared-auth';
import {
  getChatSession,
} from '@/lib/api/chatCreation.service';
import { createCelebrationWithWorkflow } from '@/lib/celebrations/createCelebrationWorkflow';
import { buildCreateWorkflowInput } from '@/lib/celebrations/workflowInput';
import { handleApiError } from '@/lib/api/client';

type StatusState = 'creating' | 'success' | 'error';

const isAuthDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const searchParams = new URLSearchParams(window.location.search);
  return import.meta.env.DEV || searchParams.get('authDebug') === '1' || window.localStorage.getItem('authDebug') === '1';
};

const authDebugLog = (message: string, payload?: Record<string, unknown>) => {
  if (!isAuthDebugEnabled()) {
    return;
  }

  if (payload) {
    console.info('[auth-debug][PostAuthStatus]', message, payload);
    return;
  }

  console.info('[auth-debug][PostAuthStatus]', message);
};

const formatMissingFieldsHint = (missingFields?: string[]): string => {
  if (!missingFields || missingFields.length === 0) {
    return '';
  }

  const labelByField: Record<string, string> = {
    _celebrant_email: 'celebrant email',
    _celebrant_role: 'celebrant role',
    celebration_type: 'celebration type',
    celebration_subtype: 'celebration subtype',
    celebrant_names: 'celebrant names',
    celebrant_roles: 'celebrant roles',
    celebrant_emails: 'celebrant emails',
    start_date: 'start date',
    end_date: 'end date',
    location_name: 'location',
    guest_count_estimate: 'guest count',
    budget: 'budget',
    style: 'style',
    culture: 'culture',
    religion: 'religion',
  };

  const labels = Array.from(new Set(missingFields.map((field) => labelByField[field] || field)));
  return labels.length > 0 ? `Missing details: ${labels.join(', ')}.` : '';
};

export const CelebrationPostAuthStatus: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(['celebrations']);

  const [status, setStatus] = useState<StatusState>('creating');
  const [message, setMessage] = useState('');
  const [redirectSeconds, setRedirectSeconds] = useState(3);
  const [retryNonce, setRetryNonce] = useState(0);
  const [authResolved, setAuthResolved] = useState(false);
  const [hasSession, setHasSession] = useState(false);

  const createStartedRef = useRef(false);
  const redirectTimerRef = useRef<number | null>(null);
  const countdownTimerRef = useRef<number | null>(null);
  const authRetryTimerRef = useRef<number | null>(null);
  const authRetryAttemptsRef = useRef(0);

  const sessionId = searchParams.get('session') ?? undefined;
  const conversationId = searchParams.get('conversationId') ?? undefined;

  const getChatReturnPath = () => {
    const params = new URLSearchParams();
    if (sessionId) {
      params.set('session', sessionId);
    }
    if (conversationId) {
      params.set('conversationId', conversationId);
    }

    const query = params.toString();
    return query ? `/celebrations/new?${query}` : '/celebrations/new';
  };

  const redirectToCentralAuth = useCallback((redirectToPath: string) => {
    if (typeof window !== 'undefined') {
      const returnUrl = new URL(redirectToPath, window.location.origin).toString();
      window.location.replace(buildAuthRedirectToUrl(returnUrl));
      return;
    }

    navigate(`/auth?redirectToPath=${encodeURIComponent(redirectToPath)}`, { replace: true });
  }, [navigate]);

  const completeWithSuccess = useCallback((celebrationId: number, warningCount = 0) => {
    setStatus('success');
    setMessage(
      warningCount > 0
        ? `Celebration created with ${warningCount} warning${warningCount === 1 ? '' : 's'}. Redirecting to details page…`
        : 'Celebration created successfully. Redirecting to details page…',
    );
    setRedirectSeconds(3);

    countdownTimerRef.current = window.setInterval(() => {
      setRedirectSeconds((prev) => {
        if (prev <= 1) {
          if (countdownTimerRef.current !== null) {
            window.clearInterval(countdownTimerRef.current);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    redirectTimerRef.current = window.setTimeout(() => {
      authDebugLog('redirecting-to-created-celebration', {
        celebrationId,
      });
      navigate(`/celebrations/${celebrationId}`, { replace: true });
    }, 3000);
  }, [navigate]);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current !== null) {
        window.clearTimeout(redirectTimerRef.current);
      }
      if (countdownTimerRef.current !== null) {
        window.clearInterval(countdownTimerRef.current);
      }
      if (authRetryTimerRef.current !== null) {
        window.clearTimeout(authRetryTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let isCancelled = false;
    const MAX_AUTH_RETRY_ATTEMPTS = 12;
    const AUTH_RETRY_DELAY_MS = 500;

    if (authRetryTimerRef.current !== null) {
      window.clearTimeout(authRetryTimerRef.current);
      authRetryTimerRef.current = null;
    }

    const resolveAuth = async () => {
      try {
        const doesSessionExist = await Session.doesSessionExist();
        if (isCancelled) {
          return;
        }

        authDebugLog('auth-resolution-check', {
          doesSessionExist,
          attempt: authRetryAttemptsRef.current + 1,
          sessionId,
          conversationId,
        });

        setHasSession(doesSessionExist);
        setAuthResolved(true);

        if (!doesSessionExist) {
          authRetryAttemptsRef.current += 1;

          if (authRetryAttemptsRef.current < MAX_AUTH_RETRY_ATTEMPTS) {
            authRetryTimerRef.current = window.setTimeout(() => {
              if (!isCancelled) {
                void resolveAuth();
              }
            }, AUTH_RETRY_DELAY_MS);
            authDebugLog('auth-not-ready-retrying', {
              nextAttempt: authRetryAttemptsRef.current + 1,
              maxAttempts: MAX_AUTH_RETRY_ATTEMPTS,
            });
            return;
          }

          authDebugLog('auth-not-ready-max-retries-reached');
        }

        authRetryAttemptsRef.current = 0;
      } catch {
        if (isCancelled) {
          return;
        }
        setHasSession(false);
        setAuthResolved(true);

        authRetryAttemptsRef.current += 1;
        if (authRetryAttemptsRef.current < MAX_AUTH_RETRY_ATTEMPTS) {
          authRetryTimerRef.current = window.setTimeout(() => {
            if (!isCancelled) {
              void resolveAuth();
            }
          }, AUTH_RETRY_DELAY_MS);
          authDebugLog('auth-resolution-error-retrying', {
            nextAttempt: authRetryAttemptsRef.current + 1,
            maxAttempts: MAX_AUTH_RETRY_ATTEMPTS,
          });
        }
      }
    };

    void resolveAuth();

    return () => {
      isCancelled = true;
      if (authRetryTimerRef.current !== null) {
        window.clearTimeout(authRetryTimerRef.current);
        authRetryTimerRef.current = null;
      }
    };
  }, [navigate, sessionId, conversationId, retryNonce]);

  useEffect(() => {
    if (!authResolved || hasSession) {
      return;
    }

    authDebugLog('auth-unresolved-after-retries-showing-error');

    setStatus('error');
    setMessage(t('celebrations:postAuthStatus.errors.sessionNotReady'));
  }, [authResolved, hasSession]);

  useEffect(() => {
    if (!authResolved || !hasSession) {
      return;
    }

    if (!sessionId) {
      setStatus('error');
      setMessage(t('celebrations:postAuthStatus.errors.sessionNotFound'));
      return;
    }

    if (createStartedRef.current) {
      return;
    }

    createStartedRef.current = true;
    setStatus('creating');
    setMessage(t('celebrations:postAuthStatus.messages.finalizing'));

    const finalizeCreation = async () => {
      try {
        authDebugLog('finalize-creation-start', {
          sessionId,
          conversationId,
        });

        const session = await getChatSession(sessionId);
        if (!session) {
          setStatus('error');
          setMessage(t('celebrations:postAuthStatus.errors.sessionExpired'));
          return;
        }

        const createInput = buildCreateWorkflowInput({
          draft: session.extractedFields ?? {},
          chatSessionId: session.sessionId,
          confirmedTags: session.confirmedTags,
          selectedEvents: session.selectedEvents,
          selectedActivities: session.selectedActivities,
          selectedGoalTemplateIdsByEvent: session.selectedGoalTemplateIdsByEvent,
        });

        if (!createInput) {
          const missingFieldsHint = formatMissingFieldsHint(session.missingFields);
          setStatus('error');
          setMessage(
            missingFieldsHint ||
            t('celebrations:postAuthStatus.errors.missingDetails'),
          );
          return;
        }

        authDebugLog('finalize-creation-using-shared-workflow', {
          sessionId,
          selectedEvents: createInput.eventSelections?.selectedEvents?.length ?? 0,
        });

        const result = await createCelebrationWithWorkflow(createInput);
        if (result.warnings.length > 0) {
          authDebugLog('shared-workflow-completed-with-warnings', {
            warningCount: result.warnings.length,
          });
        }
        completeWithSuccess(result.celebrationId, result.warnings.length);
      } catch (error) {
        const errorText = handleApiError(error);
        authDebugLog('finalize-creation-error', {
          errorText,
        });

        // Handle expired auth edge-case
        try {
          const doesSessionExist = await Session.doesSessionExist();
          if (!doesSessionExist) {
            const redirectToPath = `/celebrations/new?mode=post-auth-status&session=${encodeURIComponent(sessionId)}${
              conversationId ? `&conversationId=${encodeURIComponent(conversationId)}` : ''
            }`;
            authDebugLog('session-missing-redirecting-to-auth', {
              redirectToPath,
            });
            redirectToCentralAuth(redirectToPath);
            return;
          }
        } catch {
          // Ignore session check issues and show normal error below.
        }

        setStatus('error');
        setMessage(errorText || t('celebrations:postAuthStatus.errors.creationFailed'));
      }
    };

    void finalizeCreation();
  }, [authResolved, hasSession, sessionId, conversationId, retryNonce, completeWithSuccess, redirectToCentralAuth]);

  const title =
    status === 'creating'
      ? t('celebrations:postAuthStatus.titles.creating')
      : status === 'success'
        ? t('celebrations:postAuthStatus.titles.success')
        : t('celebrations:postAuthStatus.titles.error');

  return (
    <div className="container mx-auto h-[calc(100vh-4rem)] flex items-center justify-center p-4">
      <Card className="w-full max-w-xl p-8">
        <h1 className="text-2xl font-semibold mb-4">{title}</h1>
        <p className="text-sm text-muted-foreground mb-3">{message}</p>

        {status === 'creating' && (
          <div className="flex items-center gap-2 text-sm">
            <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
            <span>Please wait…</span>
          </div>
        )}

        {status === 'success' && (
          <p className="text-sm text-primary">{t('celebrations:postAuthStatus.messages.redirecting', { count: redirectSeconds })}</p>
        )}

        {status === 'error' && (
          <div className="flex gap-3 mt-4">
            <button
              type="button"
              onClick={() => navigate(getChatReturnPath(), { replace: true })}
              className="px-4 py-2 rounded-md border text-sm hover:bg-muted"
            >
              {t('celebrations:postAuthStatus.buttons.backToChat')}
            </button>
            <button
              type="button"
              onClick={() => {
                createStartedRef.current = false;
                setAuthResolved(false);
                setStatus('creating');
                setMessage('Retrying celebration creation…');
                setRetryNonce((prev) => prev + 1);
              }}
              className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm hover:bg-primary/90"
            >
              {t('celebrations:postAuthStatus.buttons.retry')}
            </button>
            <button
              type="button"
              onClick={() => {
                const redirectToPath = `/celebrations/new?mode=post-auth-status${
                  sessionId ? `&session=${encodeURIComponent(sessionId)}` : ''
                }${conversationId ? `&conversationId=${encodeURIComponent(conversationId)}` : ''}`;
                redirectToCentralAuth(redirectToPath);
              }}
              className="px-4 py-2 rounded-md border text-sm hover:bg-muted"
            >
              {t('celebrations:postAuthStatus.buttons.loginAgain')}
            </button>
          </div>
        )}
      </Card>
    </div>
  );
};
